import React, { useState } from 'react';
import AdminLayout from './AdminLayout';
import { useAuth } from '@/context/AuthContext';
import { isFeatureEnabled } from '@/config/featureFlags';
import {
  fetchProjectSkills,
  toggleProjectSkill,
  bulkToggleProjectSkills,
  exportProjectSkills,
  fetchTenantConfig,
  updateTenantConfig,
  fetchSkillsCatalog,
} from '@/lib/skillsApi';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Download, Loader2 } from 'lucide-react';
import type {
  ProjectSkillConfigEntry,
  ProjectSkillConfigSnapshot,
  SkillListFilters,
} from '@shared/skills';

const PAGE_SIZE = 10;

type StatusFilter = SkillListFilters['status'];

type PendingAction =
  | { mode: 'single'; skillId: string }
  | { mode: 'bulk' };

const SkillsManagementPage: React.FC = () => {
  const { user } = useAuth();
  return (
    <AdminLayout
      title="Skills Management"
      description="View usage metrics, enable/disable skills per project, and manage tenant-wide defaults."
    >
      <SkillsManagementContent userRole={user?.role ?? 'PUBLIC_FREE_USER'} tenancySeed={user?.agencyId ?? ''} />
    </AdminLayout>
  );
};

const SkillsManagementContent: React.FC<{ userRole: string; tenancySeed: string }> = ({
  userRole,
  tenancySeed,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [projectField, setProjectField] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<{ search: string; status: StatusFilter }>({
    search: '',
    status: 'all',
  });
  const [page, setPage] = useState(1);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [pendingSkillId, setPendingSkillId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [tenantField, setTenantField] = useState(tenancySeed?.toLowerCase() ?? '');
  const [activeTenantId, setActiveTenantId] = useState(tenancySeed?.toLowerCase() ?? '');
  const [tenantReason, setTenantReason] = useState('');

  const isSystemAdmin = userRole === 'SYSTEM_ADMIN';
  const advancedFlagEnabled = isFeatureEnabled('agenticAdminAdvanced');
  const showTenantOverrides = isSystemAdmin && advancedFlagEnabled;

  const projectQueryKey = ['project-skills', activeProjectId, filters, page];
  const projectQuery = useQuery({
    queryKey: projectQueryKey,
    queryFn: () =>
      fetchProjectSkills({
        projectId: activeProjectId!,
        page,
        pageSize: PAGE_SIZE,
        search: filters.search,
        status: filters.status,
      }),
    enabled: Boolean(activeProjectId),
    placeholderData: (previousData) => previousData,
  });

  const catalogQuery = useQuery({
    queryKey: ['skills-catalog'],
    queryFn: () => fetchSkillsCatalog({ pageSize: 100 }),
  });

  const tenantQuery = useQuery({
    queryKey: ['tenant-config', activeTenantId],
    queryFn: () => fetchTenantConfig(activeTenantId),
    enabled: showTenantOverrides && Boolean(activeTenantId),
  });

  const toggleMutation = useMutation({
    mutationFn: toggleProjectSkill,
    onMutate: async (variables) => {
      setPendingSkillId(variables.skillId);
      await queryClient.cancelQueries({ queryKey: projectQueryKey });
      const previous = queryClient.getQueryData<ProjectSkillConfigSnapshot>(projectQueryKey);
      if (previous) {
        queryClient.setQueryData<ProjectSkillConfigSnapshot>(projectQueryKey, (old) => {
          if (!old) return old;
          return {
            ...old,
            skills: old.skills.map((skill) =>
              skill.id === variables.skillId ? { ...skill, enabled: variables.enabled } : skill,
            ),
          };
        });
      }
      return { previous };
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(projectQueryKey, context.previous);
      }
      toast({
        title: 'Unable to update skill',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({ title: 'Skill updated' });
      void queryClient.invalidateQueries({ queryKey: projectQueryKey });
    },
    onSettled: () => setPendingSkillId(null),
  });

  const bulkMutation = useMutation({
    mutationFn: bulkToggleProjectSkills,
    onSuccess: () => {
      toast({ title: 'Bulk update applied' });
      void queryClient.invalidateQueries({ queryKey: projectQueryKey });
    },
    onError: (error) => {
      toast({
        title: 'Unable to update skills',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const tenantMutation = useMutation({
    mutationFn: updateTenantConfig,
    onSuccess: (data) => {
      toast({ title: 'Tenant overrides updated' });
      queryClient.setQueryData(['tenant-config', data.tenantId ?? activeTenantId], data);
    },
    onError: (error) => {
      toast({
        title: 'Unable to update tenant overrides',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const projectData = projectQuery.data;
  const totalPages = projectData ? Math.max(1, Math.ceil(projectData.pagination.total / PAGE_SIZE)) : 1;

  const skills = projectData?.skills ?? [];

  const handleLoadProject = (event: React.FormEvent) => {
    event.preventDefault();
    if (!projectField.trim()) {
      toast({ title: 'Project ID is required', variant: 'destructive' });
      return;
    }
    setActiveProjectId(projectField.trim());
    setPage(1);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput.trim() }));
    setPage(1);
  };

  const handleStatusChange = (value: StatusFilter) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPage(1);
  };

  const handleToggleChange = (skill: ProjectSkillConfigEntry, enabled: boolean) => {
    if (!activeProjectId || !projectData) return;
    if (!enabled) {
      setPendingAction({ mode: 'single', skillId: skill.id });
    } else {
      toggleMutation.mutate({
        projectId: activeProjectId,
        skillId: skill.id,
        enabled: true,
        version: projectData.version,
      });
    }
  };

  const confirmPendingAction = () => {
    if (!pendingAction || !activeProjectId || !projectData) {
      setPendingAction(null);
      setReasonText('');
      return;
    }
    const reason = reasonText.trim() || undefined;
    if (pendingAction.mode === 'single') {
      toggleMutation.mutate({
        projectId: activeProjectId,
        skillId: pendingAction.skillId,
        enabled: false,
        version: projectData.version,
        reason,
      });
    } else {
      bulkMutation.mutate({
        projectId: activeProjectId,
        enabled: false,
        version: projectData.version,
        applyAll: true,
        reason,
      });
    }
    setPendingAction(null);
    setReasonText('');
  };

  const handleEnableAll = () => {
    if (!activeProjectId || !projectData) return;
    bulkMutation.mutate({
      projectId: activeProjectId,
      enabled: true,
      version: projectData.version,
      applyAll: true,
    });
  };

  const handleDisableAll = () => {
    if (!activeProjectId || !projectData) return;
    setPendingAction({ mode: 'bulk' });
  };

  const handleExport = async () => {
    if (!activeProjectId) return;
    try {
      setIsExporting(true);
      const blob = await exportProjectSkills(activeProjectId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `skills-project-${activeProjectId}.json`;
      anchor.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Export ready' });
    } catch (error) {
      toast({
        title: 'Export failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleLoadTenant = (event: React.FormEvent) => {
    event.preventDefault();
    setActiveTenantId(tenantField.trim().toLowerCase());
  };

  const handleTenantOverrideChange = (skillId: string, value: 'inherit' | 'enable' | 'disable') => {
    if (!activeTenantId || !tenantQuery.data) return;
    tenantMutation.mutate({
      tenantId: activeTenantId,
      version: tenantQuery.data.version,
      reason: tenantReason.trim() || undefined,
      skills: {
        [skillId]: value === 'inherit' ? null : value === 'enable',
      },
    });
  };

  const catalogSkills = catalogQuery.data?.data ?? [];
  const tenantOverrides = tenantQuery.data?.overrides ?? {};

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>Project Scope</CardTitle>
          <CardDescription>Load a project to view or update its skill toggles.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form className="flex flex-wrap gap-3" onSubmit={handleLoadProject}>
            <div className="flex-1 min-w-[220px]">
              <Label htmlFor="project-id">Project ID</Label>
              <Input
                id="project-id"
                value={projectField}
                onChange={(event) => setProjectField(event.target.value)}
                placeholder="Enter numeric ID"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button type="submit">Load project</Button>
              {activeProjectId && (
                <Button type="button" variant="outline" onClick={() => setActiveProjectId(null)}>
                  Clear
                </Button>
              )}
            </div>
          </form>

          {activeProjectId && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3 items-end">
                <form className="flex flex-1 gap-2" onSubmit={handleSearchSubmit}>
                  <div className="flex-1 min-w-[200px]">
                    <Label htmlFor="skill-search">Search skills</Label>
                    <Input
                      id="skill-search"
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                      placeholder="Search by name or description"
                    />
                  </div>
                  <Button type="submit">Search</Button>
                </form>
                <div className="w-40">
                  <Label>Status</Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) => handleStatusChange(value as StatusFilter)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="enabled">Enabled</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="secondary" onClick={handleEnableAll} disabled={bulkMutation.isPending}>
                    Enable All
                  </Button>
                  <Button type="button" variant="destructive" onClick={handleDisableAll} disabled={bulkMutation.isPending}>
                    Disable All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    <span className="ml-2">Export JSON</span>
                  </Button>
                </div>
              </div>

              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[240px]">Skill</TableHead>
                      <TableHead>Usage (30d)</TableHead>
                      <TableHead>Metadata</TableHead>
                      <TableHead className="w-[120px] text-right">Enabled</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!projectQuery.isLoading && skills.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                          No skills match the current filters.
                        </TableCell>
                      </TableRow>
                    )}
                    {projectQuery.isLoading && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center">
                          <div className="flex items-center justify-center gap-2 text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading skills...
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {skills.map((skill) => (
                      <TableRow key={skill.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{skill.name}</span>
                              {skill.adminOnly && <Badge variant="secondary">Admin-only</Badge>}
                              {!skill.enabledByDefault && <Badge variant="outline">Off by default</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">{skill.description}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{skill.usage.count} runs</p>
                          <p className="text-xs text-muted-foreground">
                            {(skill.usage.successRate * 100).toFixed(1)}% success
                          </p>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {skill.requiredRoles.map((role) => (
                              <Badge key={`${skill.id}-role-${role}`} variant="outline">
                                {role}
                              </Badge>
                            ))}
                            {(skill.allowedProjectTypes ?? []).map((type) => (
                              <Badge key={`${skill.id}-type-${type}`} variant="secondary">
                                {type}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Switch
                            aria-label={`Toggle ${skill.name}`}
                            checked={skill.enabled}
                            disabled={
                              (toggleMutation.isPending && pendingSkillId === skill.id) ||
                              bulkMutation.isPending
                            }
                            onCheckedChange={(checked) => handleToggleChange(skill, checked)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {projectData && projectData.pagination.total > PAGE_SIZE && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {projectData.pagination.page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={page <= 1}
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={page >= totalPages}
                      onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {showTenantOverrides && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle>Tenant Overrides</CardTitle>
              <Badge variant="outline" className="text-xs">Advanced / Internal</Badge>
            </div>
            <CardDescription>Force-enable or disable skills across a tenant. This surface is gated by feature flags.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form className="flex flex-wrap gap-3" onSubmit={handleLoadTenant}>
              <div className="flex-1 min-w-[220px]">
                <Label htmlFor="tenant-id">Tenant ID</Label>
                <Input
                  id="tenant-id"
                  value={tenantField}
                  onChange={(event) => setTenantField(event.target.value)}
                  placeholder="agency or team identifier"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit">Load tenant</Button>
              </div>
            </form>

            {tenantQuery.isLoading && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading overrides...
              </p>
            )}

            {activeTenantId && !tenantQuery.isLoading && catalogSkills.length > 0 && (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="tenant-reason">Reason (optional)</Label>
                  <Input
                    id="tenant-reason"
                    value={tenantReason}
                    onChange={(event) => setTenantReason(event.target.value)}
                    placeholder="Stored in audit log"
                  />
                </div>
                <div className="max-h-[360px] overflow-auto border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Override</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {catalogSkills.map((skill) => {
                        const overrideValue = tenantOverrides[skill.id];
                        const current = overrideValue === undefined ? 'inherit' : overrideValue ? 'enable' : 'disable';
                        return (
                          <TableRow key={`tenant-${skill.id}`}>
                            <TableCell className="w-2/3">
                              <p className="font-medium">{skill.name}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">{skill.description}</p>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={current}
                                onValueChange={(value: 'inherit' | 'enable' | 'disable') =>
                                  handleTenantOverrideChange(skill.id, value)
                                }
                                disabled={tenantMutation.isPending}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="inherit">Inherit default</SelectItem>
                                  <SelectItem value="enable">Force enable</SelectItem>
                                  <SelectItem value="disable">Force disable</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={pendingAction !== null} onOpenChange={(open) => {
        if (!open) {
          setPendingAction(null);
          setReasonText('');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm disable</AlertDialogTitle>
            <AlertDialogDescription>
              Provide an optional reason for audit logging. This action takes effect immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="disable-reason">Reason</Label>
            <Textarea
              id="disable-reason"
              value={reasonText}
              onChange={(event) => setReasonText(event.target.value)}
              placeholder="Optional notes"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setPendingAction(null);
              setReasonText('');
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmPendingAction}>
              Disable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SkillsManagementPage;
